/**
 * LodResidencyManager — which chunk, at which LOD, covers each region of the world right now.
 *
 * Port of worldify-app's renderedChunkCoords / renderedChunkChildren / updateIsParentVisible
 * scheme, restructured per this repo's drift doc: instead of co-maintained "subtree rendered" sets
 * (which need forget-on-unload contracts), coverage is DERIVED on every selection pass from live
 * state — chunk mesh phase (via deps.isRendered), column info, and the BFS want-set — with a memo
 * for the pass. Selection passes run only on visibility-dirty frames, and the whole tree is a few
 * thousand nodes, so recomputation is cheap and cannot drift.
 *
 * THE SWAP RULE (never a gap, never an overlap):
 *  - covered(chunk) = its volume is fully represented — by its own complete mesh, by provable
 *    emptiness/solidity (sky above the column top; coarse ground below the lowest surface), by
 *    BFS-pruning at the base level (the volume is unseeable), or by ALL of its in-range children
 *    being covered (recursively).
 *  - selection walks top-down from the coarsest roots: a node whose in-range children are NOT all
 *    covered (or that has no in-range children) is DRAWN and its descendants are suppressed; a node
 *    whose children fully cover it recurses. A parent therefore stays visible until the exact pass
 *    where its 8th child subtree completes, and a visible parent structurally excludes descendants.
 *  - the octet invariant from LodShellSelector guarantees the walk partitions the covered volume.
 */

import {
  parseChunkKey,
  POSITIVE_MARGIN_OFFSETS_7,
  packChunkKeyLod,
  unpackChunkKeyLod,
} from '@worldify/shared';
import {
  computeLodShells,
  packCoord,
  unpackCoord,
  type LodShellResult,
} from './LodShellSelector.js';

/**
 * The walk's verdict: position+lod → visible. Keyed by PACKED INTEGER per lod, not by a
 * `cx,cy,cz,lod` string: the walk writes one entry per reached node (~2.5k at a coarse base) and the
 * render gate reads one per geometry every frame, and building a key string for each was pure
 * allocation on the hottest path in the frame — the same reason chunk lookups and column probes are
 * packed (see packCoord / packColumn).
 *
 * Per-lod maps because folding lod into packCoord would exceed MAX_SAFE_INTEGER.
 */
export class LodSelection {
  // ONE flat map keyed by the lod-aware packed key — the per-lod array-of-maps shape existed only
  // because packCoord couldn't carry lod (see shared packedKey.packChunkKeyLod).
  private readonly byKey: Map<number, boolean> = new Map();

  set(cx: number, cy: number, cz: number, lod: number, visible: boolean): void {
    this.byKey.set(packChunkKeyLod(cx, cy, cz, lod), visible);
  }

  /** undefined = the walk never reached this position (⇒ suppressed by a drawn ancestor). */
  get(cx: number, cy: number, cz: number, lod: number): boolean | undefined {
    return this.byKey.get(packChunkKeyLod(cx, cy, cz, lod));
  }

  has(cx: number, cy: number, cz: number, lod: number): boolean {
    return this.byKey.has(packChunkKeyLod(cx, cy, cz, lod));
  }

  get size(): number {
    return this.byKey.size;
  }

  /** Every entry with its coords unpacked. For DEV invariants and diagnostics — not a hot path. */
  *entries(): Generator<{ cx: number; cy: number; cz: number; lod: number; visible: boolean }> {
    for (const [packed, visible] of this.byKey) {
      const { cx, cy, cz, lod } = unpackChunkKeyLod(packed);
      yield { cx, cy, cz, lod, visible };
    }
  }

  /**
   * Key-string accessors for tests and diagnostics, which are written in terms of chunk keys (the
   * domain language). NEVER call these on a per-frame path — parsing a key defeats the packing.
   */
  getByKey(key: string): boolean | undefined {
    const { cx, cy, cz, lod } = parseChunkKey(key);
    return this.get(cx, cy, cz, lod);
  }

  hasByKey(key: string): boolean {
    const { cx, cy, cz, lod } = parseChunkKey(key);
    return this.has(cx, cy, cz, lod);
  }
}

export interface LodResidencyDeps {
  /**
   * THE base-level (lod 0) coverage verdict, in full — the walk adds nothing to it (in particular
   * it does NOT OR-in isRendered: a mesh only counts where the implementation says the render
   * gate will actually draw it, e.g. inside the BFS box — an outside-box mesh releasing its
   * parent was a hole regression). CONTRACT: return true iff the chunk will be DRAWN by the
   * render gate this frame, or its volume is unseeable (provable sky / occlusion-pruned).
   * "Won't draw" (unloaded, queued, incomplete) must return false so the parent keeps covering.
   */
  isBaseCovered(cx: number, cy: number, cz: number): boolean;
  /**
   * Coarse-level external coverage from the lod column probes: above maxCy (sky) or below minCy
   * (provably fully solid — coarse generation has no caves, so this is safe at lod ≥ 1).
   */
  isCoarseCovered(cx: number, cy: number, cz: number, lod: number): boolean;
  /**
   * True when the chunk's mesh has been applied complete (ChunkPhase.MeshedComplete). Takes
   * COORDINATES, not a key: this is the hottest call in the walk (~150k per pass at a coarse base
   * before the retired-walk gate below), and building a `cx,cy,cz,lod` string per call made the
   * lookup allocate — the allocation and the GC it caused, not the hash, were the cost.
   */
  isRendered(cx: number, cy: number, cz: number, lod: number): boolean;
  /**
   * THE RETIRED CHUNKS at `lod`, as packed coords (LodShellSelector.packCoord) — resident, meshed
   * complete, and OUTSIDE the level's in-range set. Null or empty when the level holds none.
   *
   * The fallback used to be found by SEARCHING for them: every rim child of every level fanned out
   * to depth 2 (73 nodes) asking `isRendered` at each, gated only by a per-band "is anything retired
   * in [lod−2, lod]" flag. At a lod-0 base that band reaches lod 0 from the lod-2 and lod-3 rims, and
   * lod 0 ALWAYS has retired meshes while the centre moves (the chunks the pan just left behind) — so
   * a pan opened the gate on every pass and the walk scanned ~200k positions to find ~100 chunks.
   * Measured in-app: the walk went 3.5 ms → 17.6 ms the moment 97 lod-0 chunks retired, on every
   * streaming frame. Enumerating the retired chunks themselves is O(retired), and the producer
   * (VoxelWorld.recomputeRetiredMeshIndex) was already scanning residency once per pass to answer
   * the old boolean — the list costs it nothing extra.
   *
   * Both qualifying terms are load-bearing, as before: without MESHED the sacrificial +margin ring
   * (permanently resident, permanently out of range, never meshed) would be a candidate forever;
   * without OUT-OF-RANGE this would be the in-range walk's own set.
   */
  retiredMeshesAt(lod: number): Iterable<number> | null;
  /**
   * The chunk row (cy) of the given level's SURFACE TOP for the column (cx, cz) — the topmost
   * content chunk per that level's own column probe (lod 0: the columnInfo tile; lod ≥ 1:
   * lodColumnInfo.maxCy) — or null while the probe hasn't arrived. Feeds the vertical surface
   * anchor: each level samples the heightfield on its own lattice, so the rendered surface can
   * sit in a DIFFERENT chunk row at the finer level.
   */
  finerSurfaceTopCy(cx: number, cz: number, lod: number): number | null;
  /**
   * Is this chunk CONTRIBUTING PIXELS RIGHT NOW (the display truth, not the wanted state)?
   *
   * Feeds THE NO-STEAL RULE: a coarse node that has just finished meshing may cover volume that
   * nothing is drawing, but it may not TAKE THE SCREEN from finer chunks the user can already
   * see. Without that rule, cold start flickers by construction — the walk hands the screen to
   * lod 0 first (nothing coarse is meshed yet), then the first coarse mesh to arrive over a
   * half-streamed octet draws and suppresses its whole subtree, and releases again when the octet
   * completes: lod0 → coarse → lod0 on every octet, which is exactly the reported initial-load
   * flicker.
   *
   * OPTIONAL, defaulting to "nothing is displayed", which reproduces the pre-rule behaviour
   * exactly — so a caller that has no display layer (or a test that does not model one) is
   * unaffected. Production wires it to `TerrainBatch.isChunkDisplayed`; the coverage harness
   * wires it to the previous tick's displayed set.
   */
  isDisplayed?(cx: number, cy: number, cz: number, lod: number): boolean;
  /**
   * A FINISHED MESH WAITING FOR ITS APPLY (RemeshPipeline.hasPendingApply). Counted as covering: the
   * apply drain is paced by the frame clock, so a parent's mesh could land in a frame where a child's
   * finished result was still queued, and the walk let the parent take over for exactly that frame -
   * the one-frame flash of the step level. The hide-hold rule keeps the previous owner drawing until
   * the child actually displays, so counting it here costs no frame of darkness.
   */
  isMeshPending?(cx: number, cy: number, cz: number, lod: number): boolean;
  /**
   * PROVABLY NOTHING TO DRAW at this position — open sky above the column's content top, provably
   * solid below its lowest surface, or occlusion-pruned at the base level. The resolution census's
   * second term (see computeRegionResolution): a position nothing will ever draw is not a hole and
   * must not hold a level open, which is the trap the naive "all wanted chunks displayed" falls in.
   *
   * OPTIONAL, defaulting to "nothing is provable" — a caller with no column probes is unaffected.
   */
  isProvablyEmpty?(cx: number, cy: number, cz: number, lod: number): boolean;
  /**
   * UNMESHED BY DESIGN at the base level: the chunk's data is resident and the render set refuses
   * to build a mesh for it (VoxelWorld.shouldMeshChunk's occlusion path). The resolution census's
   * fifth term, and it is NOT the same question as `isProvablyEmpty`.
   *
   * `baseChunkState` deliberately classifies these 'waiting' rather than 'occluded' while the BFS
   * frontier is live — cell by cell, "unreached" is indistinguishable from "behind rock that has
   * not streamed yet", and counting them COVERED is what flickered lod0↔lod1 on load (bug M1). But
   * "may a parent release over it" and "has this level delivered its region" are different
   * questions: the level has decided not to draw this position, so nothing it could do would ever
   * resolve it further, and holding the region open on it would stall the level above on work that
   * is never going to happen. Coverage keeps the strict answer; the census gets the honest one.
   * (A resident lod-0 chunk still WAITING ON ITS +MARGIN SOURCES reads the same in the debug state
   * string and is not this — see debugChunkStreamState, which now separates 'parked' from
   * 'margins' precisely because the two were one word.)
   *
   * OPTIONAL, defaulting to "nothing is parked" — a caller with no render set is unaffected.
   */
  isOcclusionParked?(cx: number, cy: number, cz: number): boolean;
  /**
   * Every chunk the display layer OWNS right now, enumerated. The census's drawn-by-a-finer-level
   * term needs the ANCESTORS of displayed finer chunks (a retired lod-0 chunk still drawing under a
   * coarse position resolves it), and a point probe cannot answer that without an 8^k walk.
   *
   * DISPLAY OWNERSHIP, not pixels: a chunk whose mesh is empty (fully solid, no surface) has taken
   * responsibility for its volume and there is nothing left to draw there, so it resolves. That is
   * the opposite of the no-steal rule's question, which is why this is not `isDisplayed`.
   */
  forEachDisplayed?(cb: (cx: number, cy: number, cz: number, lod: number) => void): void;
}

/**
 * ONE DISPLAYABLE REGION'S RESOLUTION. A_k is level k's range minus the parents of level k−1's
 * range — the annulus level k owns outright (A_base is the base level's whole box).
 */
export interface RegionCensus {
  lod: number;
  wanted: number;
  resolved: number;
  /** Of `wanted`, the four ways a position resolves — drawn at this level, provably nothing to
   *  draw, this level's own applied mesh covering it while the walk suppresses it, or a finer
   *  level drawing it (the retired fallback). */
  displayed: number;
  empty: number;
  meshed: number;
  finer: number;
  /** ...and the fifth: resident at the base level, deliberately never meshed because the render set
   *  refuses it (see isOcclusionParked). */
  parked: number;
  /** One unresolved position as `x,y,z`, for diagnostics; empty when the region is resolved. */
  sample: string;
}

/**
 * ONE LEVEL'S IN-RANGE SET AS A DENSE GRID over its own bounding box, plus a coverage memo on the
 * same lattice.
 *
 * The walk's innermost operation is "is this position in that level's range", and it was a
 * `Set<number>` probe keyed by packChunkKey — which reaches ~2^51, far outside V8's small-integer
 * range, so every probe took the boxed-double hash path rather than the Smi one. The onion's levels
 * are box-shaped by construction (each is the octet-rounded 1-dilation of the level below), so the
 * dense form is barely larger than the set's own element count and turns the probe into an index
 * computation and a byte read.
 *
 * `memo` rides the same lattice for the same reason: coverage verdicts were a per-level Map keyed the
 * same way, allocated fresh every pass and filled with thousands of entries of garbage.
 */
interface LevelLattice {
  minX: number; minY: number; minZ: number;
  sx: number; sy: number; sz: number;
  /** 1 where the level's in-range set holds this position. */
  inRange: Uint8Array;
  /** Per-pass coverage verdicts: 0 unknown, 1 covered, 2 not covered. */
  memo: Int8Array;
  /** Per-pass no-steal verdicts, same encoding. */
  displayed: Int8Array;
  /** Per-pass vertical-surface-anchor verdicts, same encoding — 2D (x,z) over sx*sz, because the
   *  anchor is a property of the COLUMN, not of the node's row. */
  surface: Int8Array;
}

export class LodResidencyManager {
  private shells: LodShellResult | null = null;
  private desiredLoadSets: Map<number, Set<number>> = new Map();

  /**
   * Dense mirror of `shells.levels`, rebuilt only when the shells change — a chunk-cross event, not
   * a per-frame one — so the walk never pays for building it. The Set form stays the public one
   * (`levelInRange`) because every consumer outside the walk iterates it rather than probing it.
   */
  private lattices: LevelLattice[] = [];

  private baseLod = 0;
  private baseRadius = -1;
  private farLevels = 0;
  private centerCx = NaN;
  private centerCy = NaN;
  private centerCz = NaN;

  constructor(private readonly deps: LodResidencyDeps) {}

  /** Far rings are active (farLevels > 0). When inactive the world renders exactly as before. */
  get active(): boolean {
    return this.farLevels > 0 && this.shells !== null;
  }

  get maxLod(): number {
    return this.baseLod + this.farLevels;
  }

  get currentBaseLod(): number {
    return this.baseLod;
  }

  /**
   * Recompute the shells when the center crossed a base-level chunk or any parameter changed.
   * @param baseRadius Chebyshev half-extent of the base region in base-lod chunks. Pass the
   *   visibility radius + 1 so the base set contains everything the render gate can draw
   *   (reachable ∪ face-ring ⊆ radius+1) — a drawable base chunk outside the managed set would
   *   double-draw with its parent.
   * @returns true when the shells changed.
   */
  update(
    centerWorld: { x: number; y: number; z: number },
    baseLod: number,
    baseRadius: number,
    farLevels: number,
  ): boolean {
    if (farLevels <= 0) {
      const changed = this.shells !== null;
      this.shells = null;
      this.desiredLoadSets.clear();
      this.lattices = [];
      this.farLevels = 0;
      return changed;
    }

    const baseChunkWorld = 8 * (1 << baseLod); // CHUNK_WORLD_SIZE * 2^baseLod
    const ccx = Math.floor(centerWorld.x / baseChunkWorld);
    const ccy = Math.floor(centerWorld.y / baseChunkWorld);
    const ccz = Math.floor(centerWorld.z / baseChunkWorld);

    if (
      this.shells &&
      baseLod === this.baseLod &&
      baseRadius === this.baseRadius &&
      farLevels === this.farLevels &&
      ccx === this.centerCx && ccy === this.centerCy && ccz === this.centerCz
    ) {
      return false;
    }

    this.baseLod = baseLod;
    this.baseRadius = baseRadius;
    this.farLevels = farLevels;
    this.centerCx = ccx;
    this.centerCy = ccy;
    this.centerCz = ccz;
    this.shells = computeLodShells({ centerWorld, baseLod, baseRadius, farLevels });
    this.desiredLoadSets.clear();
    this.buildLattices();
    return true;
  }

  /**
   * Build the dense mirror. One pass over each level's set to find its box, one to mark it — paid
   * only when the shells change, which is a chunk cross or a parameter change.
   *
   * The memo and no-steal arrays are allocated with it rather than per pass: they are indexed on the
   * same lattice, so their size is fixed by the shells too, and a pass only has to zero them.
   */
  private buildLattices(): void {
    if (!this.shells) { this.lattices = []; return; }
    // A PAN SHIFTS THE BOX WITHOUT RESIZING IT, and shells change on every chunk cross — so the
    // common case is the same extents at a new origin, and reallocating three arrays per level for
    // it would make a fast pan an allocation storm. Same size, same buffers.
    const prev = this.lattices;
    this.lattices = [];
    let li = -1;
    for (const level of this.shells.levels) {
      li++;
      let minX = 0, minY = 0, minZ = 0, sx = 0, sy = 0, sz = 0;
      if (level.inRange.size > 0) {
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        minX = Infinity; minY = Infinity; minZ = Infinity;
        for (const p of level.inRange) {
          const [x, y, z] = unpackCoord(p);
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
          if (z < minZ) minZ = z;
          if (z > maxZ) maxZ = z;
        }
        sx = maxX - minX + 1; sy = maxY - minY + 1; sz = maxZ - minZ + 1;
      }
      const cells = sx * sy * sz;
      const reuse = prev[li];
      const same = reuse !== undefined && reuse.sx === sx && reuse.sy === sy && reuse.sz === sz;
      const inRange = same ? reuse.inRange : new Uint8Array(cells);
      if (same) inRange.fill(0);
      for (const p of level.inRange) {
        const [x, y, z] = unpackCoord(p);
        inRange[((z - minZ) * sy + (y - minY)) * sx + (x - minX)] = 1;
      }
      this.lattices.push({
        minX, minY, minZ, sx, sy, sz, inRange,
        // Zeroed per pass by computeSelection, so they need no clearing here.
        memo: same ? reuse.memo : new Int8Array(cells),
        displayed: same ? reuse.displayed : new Int8Array(cells),
        surface: same ? reuse.surface : new Int8Array(sx * sz),
      });
    }
  }

  /** In-range set for a lod level (packed coords), or null when the level isn't part of the onion. */
  levelInRange(lod: number): Set<number> | null {
    if (!this.shells) return null;
    const idx = lod - this.baseLod;
    return this.shells.levels[idx]?.inRange ?? null;
  }

  /**
   * The onion's center in `lod`-level chunk coordinates (base center right-shifted — >> floors for
   * negatives, matching parentOf), or null while inactive. Used by the LodAudit distance-order
   * checks; not part of the walk itself.
   */
  levelCenter(lod: number): [number, number, number] | null {
    if (!this.shells) return null;
    const shift = lod - this.baseLod;
    if (shift < 0) return null;
    return [this.centerCx >> shift, this.centerCy >> shift, this.centerCz >> shift];
  }

  isInRange(cx: number, cy: number, cz: number, lod: number): boolean {
    return this.levelInRange(lod)?.has(packCoord(cx, cy, cz)) ?? false;
  }

  /**
   * The level's LOAD set: inRange ∪ its positive-margin dilation. The +7 ring is SACRIFICIAL —
   * loaded so every in-range chunk's high-side margin sources exist (→ MeshedComplete, → parents
   * can always swap; no permanently-holed shell edges), but never meshed or rendered itself. This
   * is the per-level generalization of the LOD-0 contract's `render ⊆ load − 1 ring`.
   */
  desiredLoadSet(lod: number): Set<number> | null {
    if (!this.shells) return null;
    let set = this.desiredLoadSets.get(lod);
    if (set) return set;
    const inRange = this.levelInRange(lod);
    if (!inRange) return null;
    set = new Set(inRange);
    for (const packed of inRange) {
      const [x, y, z] = unpackCoord(packed);
      for (const [dx, dy, dz] of POSITIVE_MARGIN_OFFSETS_7) {
        set.add(packCoord(x + dx, y + dy, z + dz));
      }
    }
    this.desiredLoadSets.set(lod, set);
    return set;
  }

  /**
   * True when every in-range chunk of a level is either meshed complete or provably sky/solid —
   * i.e. the level can act as the visual fallback for everything finer. Gates coarsening steps of
   * the base LOD: the finer level may only leave the onion once this holds, or holes would show.
   */
  isLevelFullyCovered(lod: number): boolean {
    const range = this.levelInRange(lod);
    if (!range) return false;
    for (const packed of range) {
      const [x, y, z] = unpackCoord(packed);
      if (this.deps.isRendered(x, y, z, lod)) continue;
      if (this.deps.isCoarseCovered(x, y, z, lod)) continue;
      return false;
    }
    return true;
  }

  /**
   * THE RESOLUTION CENSUS — per displayable region, how much of it is actually settled.
   *
   * The predicate this replaces asked the QUEUES ("nothing unissued, nothing in flight, no
   * dispatchable mesh"), and a level whose data has all arrived and whose chunks are all still
   * hidden answers yes to every one of those. Measured in the headless stream oracle as a level
   * reported complete with 512 positions wanted and 0 on screen — so the fine-first ceiling
   * advanced off a level that owned nothing.
   *
   * RESOLVED is asked per POSITION instead: drawn at this level ∨ provably nothing to draw ∨ THIS
   * LEVEL'S OWN MESH covering it (the walk's `covered`, sans the recursive children term) ∨ drawn by
   * a finer level (the retired fallback) ∨ resident and unmeshed BY DESIGN at the base level (see
   * isOcclusionParked) ∨ no longer wanted (not enumerated — it left the range).
   * A_k is resolved only when all of it is.
   *
   * THE OWN-MESH TERM IS NOT A LOOPHOLE, IT IS THE DIFFERENCE between "this level is still
   * streaming" and "this level owns every pixel", and only the first is a streaming question. The
   * walk deliberately hides a chunk whose volume a coarser node is drawing — that is the decimation
   * staircase, whose own signal is `deficitByLod` — so a base region under a drawn coarse ring can
   * never reach "all displayed", and a ceiling waiting for it deadlocks: nothing coarser may stream,
   * so the coarse ring is never replaced, so the base chunks stay hidden (measured as an 8.4 s
   * freeze with all twelve workers idle). A position with an APPLIED COMPLETE mesh has had
   * everything asked of the level that owns it; a position with no mesh and no clip has not, which
   * is what keeps the drain predicate's lie caught.
   *
   * RE-DERIVED ON EVERY CALL, never latched: every term reads live state, and a cached one would be
   * a fourth writer on the display truth (see rebuildDisplayCoverIndex's note on why that lies).
   * Callers that ask twice in a pass should hold the returned rows, not ask again.
   */
  computeRegionResolution(): RegionCensus[] {
    const out: RegionCensus[] = [];
    if (!this.shells) return out;
    const maxLod = this.maxLod;

    // The display truth, once: positions drawn at each level, and every ANCESTOR of a drawn node.
    const drawnAt: Array<Set<number>> = [];
    const drawnBelow: Array<Set<number>> = [];
    this.deps.forEachDisplayed?.((cx, cy, cz, lod) => {
      (drawnAt[lod] ??= new Set<number>()).add(packCoord(cx, cy, cz));
      let x = cx, y = cy, z = cz;
      for (let l = lod + 1; l <= maxLod; l++) {
        x >>= 1; y >>= 1; z >>= 1;
        (drawnBelow[l] ??= new Set<number>()).add(packCoord(x, y, z));
      }
    });

    // The level's own local coverage verdict, one copy of `covered`'s non-recursive terms: at lod 0
    // the base classification owns it whole (sky / occluded / drawable — see isBaseCovered), above
    // it an applied complete mesh or the column clip.
    const ownMesh = (x: number, y: number, z: number, lod: number): boolean => lod === 0
      ? this.deps.isBaseCovered(x, y, z)
      : this.deps.isRendered(x, y, z, lod) || this.deps.isCoarseCovered(x, y, z, lod);

    for (let lod = 0; lod <= maxLod; lod++) {
      const row: RegionCensus = {
        lod, wanted: 0, resolved: 0, displayed: 0, empty: 0, meshed: 0, finer: 0, parked: 0,
        sample: '',
      };
      out[lod] = row;
      const range = this.levelInRange(lod);
      if (!range) continue;
      // THE INTERIOR belongs to the finer level, not to this one. By the octet invariant a position
      // is a parent of the finer range iff its first (even-coordinate) child is in it — so the
      // membership test is one probe rather than a set built from the level below.
      const finerRange = this.levelInRange(lod - 1);
      const here = drawnAt[lod];
      const below = drawnBelow[lod];
      for (const packed of range) {
        const [x, y, z] = unpackCoord(packed);
        if (finerRange?.has(packCoord(x << 1, y << 1, z << 1))) continue;
        row.wanted++;
        if (here?.has(packed)) { row.displayed++; row.resolved++; continue; }
        if (this.deps.isProvablyEmpty?.(x, y, z, lod)) { row.empty++; row.resolved++; continue; }
        if (ownMesh(x, y, z, lod)) { row.meshed++; row.resolved++; continue; }
        if (below?.has(packed)) { row.finer++; row.resolved++; continue; }
        if (lod === 0 && this.deps.isOcclusionParked?.(x, y, z)) {
          row.parked++; row.resolved++; continue;
        }
        if (!row.sample) row.sample = `${x},${y},${z}`;
      }
    }
    return out;
  }

  /** Is region A_lod fully resolved? A level the onion does not hold owns nothing, so it is.
   *  Recomputes the WHOLE census — a caller asking about several levels holds the rows instead. */
  regionResolved(lod: number): boolean {
    const row = this.computeRegionResolution()[lod];
    return row === undefined || row.resolved >= row.wanted;
  }

  /**
   * Top-down visibility selection over every level. Returns key → visible for the chunks the walk
   * REACHED; any in-range chunk absent from the map is suppressed (a drawn ancestor covers it).
   * Base-level verdicts still need ANDing with the occlusion/completeness render gate by the
   * caller — this walk decides LOD ownership of volume, not sight.
   */
  /**
   * Per-level node visit counts from the last computeSelection, indexed by LOD (sparse). The walk
   * reads 13 ms/frame at a coarse base and a single timing can't say whether that's onion DEPTH (in
   * which case it bounds how many far rings are affordable) or one pathological level. Counters, not
   * timings: an increment can't distort what it measures, and per-level counts localize it.
   */
  readonly lastVisitsByLod: number[] = [];
  /** Coverage verdicts computed (memo misses) in the last walk — the walk's dominant unit of work. */
  lastCoverageCalls = 0;
  /**
   * THE DECIMATION BACKLOG, per level: drawn coarse nodes whose in-range finer children are wanted
   * but not covered — i.e. "a coarse chunk is on screen where finer ones belong". Counted at the two
   * takeover branches, which is where the walk already decides exactly that.
   *
   * The two terms that qualify it are what keep the cold path out of it: `hasChild` false is the rim
   * (coarse IS the rightful level there, nothing to decimate), and an unrendered node means nothing
   * is on screen to step down from — a fresh load, where fine-first is correct.
   */
  readonly deficitByLod: number[] = [];

  /**
   * COARSE NODES THAT NEED A MESH AND HAVE NONE — the walk reached them, their children do not cover
   * the volume, and nothing stops them drawing except having nothing to draw. This is what makes
   * coarse meshing DEMAND-DRIVEN instead of geometry-guessed: the interior of the onion is a fallback
   * that fine-first ordering guarantees will never be needed, so it is not meshed speculatively, and
   * this set is how the rare case that DOES need it (a fine chunk unloaded, edited, or evicted under
   * a coarse parent) asks for one. Keyed with packChunkKeyLod, like LodSelection.
   */
  private readonly wantsMesh = new Set<number>();

  /** Does the last walk want a mesh at this coarse position? Read by VoxelWorld.shouldMeshChunk. */
  wantsMeshAt(cx: number, cy: number, cz: number, lod: number): boolean {
    return this.wantsMesh.has(packChunkKeyLod(cx, cy, cz, lod));
  }

  computeSelection(): LodSelection {
    const out = new LodSelection();
    this.lastVisitsByLod.length = 0;
    this.lastCoverageCalls = 0;
    this.deficitByLod.length = 0;
    this.wantsMesh.clear();
    if (!this.shells) return out;

    const levels = this.shells.levels;
    const last = levels.length - 1;
    const lat = this.lattices;
    // The memos live on the shells' lattices (see LevelLattice) and only need zeroing per pass —
    // they used to be a Map per level, allocated fresh and filled with thousands of entries every
    // pass, each keyed by a packed coord too large to be a V8 small integer.
    for (let i = 0; i < lat.length; i++) {
      lat[i].memo.fill(0);
      lat[i].displayed.fill(0);
      lat[i].surface.fill(0);
    }

    /**
     * Lattice index of a position at level `li`, or -1 when it lies outside that level's box (which
     * means outside its range: the box is the range's own bounding box).
     */
    const at = (li: number, x: number, y: number, z: number): number => {
      const L = lat[li];
      if (L === undefined) return -1;
      const ix = x - L.minX, iy = y - L.minY, iz = z - L.minZ;
      if (ix < 0 || iy < 0 || iz < 0 || ix >= L.sx || iy >= L.sy || iz >= L.sz) return -1;
      return (iz * L.sy + iy) * L.sx + ix;
    };

    /** ...and of a COLUMN at level `li`, for the surface-anchor memo. */
    const atXZ = (li: number, x: number, z: number): number => {
      const L = lat[li];
      if (L === undefined) return -1;
      const ix = x - L.minX, iz = z - L.minZ;
      if (ix < 0 || iz < 0 || ix >= L.sx || iz >= L.sz) return -1;
      return iz * L.sx + ix;
    };

    /** In range at level `li`? An index computation and a byte read. */
    const inRange = (li: number, x: number, y: number, z: number): boolean => {
      const i = at(li, x, y, z);
      return i >= 0 && lat[li].inRange[i] === 1;
    };

    /**
     * THE OCTET INVARIANT, ASKED ONCE INSTEAD OF EIGHT TIMES. A parent's eight children are either
     * ALL in the finer level's range or NONE are — every level is the octet-rounded 1-dilation of the
     * level below, so a partial octet cannot exist (pinned by lodShellSelector.test.ts, both for the
     * default rings and at the deepest far-view setting). The first child, whose coordinates are the
     * parent's doubled and therefore even, answers for the whole octet.
     *
     * Five loops used to run this test per child — covered, childCoverage, the visit recursion,
     * anyDisplayedBelow and requestFinerStep — which with the surface anchor's probes came to 24-44
     * probes per node where the octet needs one.
     */
    const octetInRange = (bx: number, by: number, bz: number, li: number): boolean =>
      li >= 0 && inRange(li, bx, by, bz);

    const covered = (cx: number, cy: number, cz: number, li: number): boolean => {
      const lod = levels[li].lod;
      const slot = at(li, cx, cy, cz);
      // Out of the level's box is out of its range, and every caller already gated on that — so this
      // is unreachable rather than a case. Compute without memoizing if it ever is reached.
      if (slot >= 0) {
        const memo = lat[li].memo[slot];
        if (memo !== 0) return memo === 1;
      }
      this.lastCoverageCalls++;

      let v: boolean;
      if (li === 0) {
        // Base-level coverage. At lod 0 the dep is the WHOLE verdict (own-mesh counting included —
        // see the isBaseCovered contract; OR-ing isRendered here bypassed the dep's box test and
        // released parents over outside-box meshes). A coarse base level (explore zoomed out)
        // keeps rendered ∨ column-clip — there is no BFS/box at lod ≥ 1.
        v = lod === 0
          ? this.deps.isBaseCovered(cx, cy, cz)
          : this.deps.isRendered(cx, cy, cz, lod) || this.deps.isCoarseCovered(cx, cy, cz, lod)
            || !!this.deps.isMeshPending?.(cx, cy, cz, lod);
      } else if (this.deps.isCoarseCovered(cx, cy, cz, lod)) {
        v = true;
      } else if (this.deps.isRendered(cx, cy, cz, lod)) {
        // Its own complete mesh covers the volume.
        v = true;
      } else if (this.deps.isMeshPending?.(cx, cy, cz, lod)) {
        v = true;
      } else {
        // Covered iff it HAS in-range children and every one of them is covered (A node with no
        // in-range children — the outer ring — covers nothing unless rendered/provable above),
        // AND the finer level's surface anchor holds (see finerSurfaceCovered).
        // ONE COPY OF THE RELEASE RULE, shared with the walk — this branch used to be a second,
        // textually separate implementation of it, which the file's own history says is exactly how
        // the two drifted apart before.
        const hasChild = octetInRange(cx << 1, cy << 1, cz << 1, li - 1);
        v = hasChild && childrenCover(cx, cy, cz, li, hasChild);
      }
      if (slot >= 0) lat[li].memo[slot] = v ? 1 : 2;
      return v;
    };

    /**
     * THE VERTICAL SURFACE ANCHOR — what makes LOD swaps atomic ACROSS chunk rows. Each level
     * samples the heightfield on its own (decimated) lattice, so the rendered surface HEIGHT
     * SHIFTS between levels; near a chunk-row boundary — and further on steep terrain — the finer
     * level's surface lands in a DIFFERENT row, possibly under a vertically-adjacent, clipped
     * (never-drawn) sibling whose subtree gates nothing. Releasing a coarse chunk on its own
     * children alone then removes the coarse surface while the replacement surface, one row away
     * in someone else's subtree, hasn't meshed (the height-dip gap). So: a node may count as
     * covered-by-children ONLY when the finer level's surface-top chunk of each footprint column
     * is itself covered, wherever that row lies — covered() is position-based and memoized, so
     * cross-subtree queries are free. Unknown column info waits (the probe is coming);
     * out-of-range anchors belong to the coarser rim and are skipped.
     */
    const finerSurfaceCovered = (cx: number, cz: number, li: number): boolean => {
      // MEMOIZED, and it was not before: both callers of the release rule asked it for the same
      // column, so every node paid the four column probes twice. It reads only stable probe data and
      // memoized coverage, so it is constant within a pass.
      const col = atXZ(li, cx, cz);
      if (col >= 0) {
        const memo = lat[li].surface[col];
        if (memo !== 0) return memo === 1;
      }
      const v = finerSurfaceUncached(cx, cz, li);
      if (col >= 0) lat[li].surface[col] = v ? 1 : 2;
      return v;
    };

    const finerSurfaceUncached = (cx: number, cz: number, li: number): boolean => {
      const finerLod = levels[li - 1].lod;
      const bx = cx << 1, bz = cz << 1;
      for (let i = 0; i < 4; i++) {
        const u = bx + (i & 1), w = bz + ((i >> 1) & 1);
        const top = this.deps.finerSurfaceTopCy(u, w, finerLod);
        if (top === null) return false;
        // NOT one of this node's octet — the anchor row can be anywhere, which is the whole point of
        // the vertical anchor — so the octet invariant says nothing here and this stays a probe.
        if (inRange(li - 1, u, top, w) && !covered(u, top, w, li - 1)) return false;
      }
      return true;
    };

    /**
     * RETIRED FALLBACK — draw resident, meshed chunks at positions OUTSIDE the current in-range
     * sets. When the base LOD refines (or the center pans), every level's range shrinks/shifts in
     * the same pass; chunks that were covering on-screen volume fall out of range and would
     * otherwise vanish immediately, while the coarser node that now owns the volume may not be
     * meshed yet — the "gap on LOD switch" bug. A retired chunk drawn here self-resolves: the
     * moment its owning ancestor renders, the walk stops visiting it (suppressed by default) and
     * the eviction pass unloads it. Top-down, first rendered node wins (no overlap within the
     * retired subtree); depth-capped — deeper leftovers are rare and heal on the next passes.
     */
    /**
     * RETIRED-FALLBACK ROOTS. Where the old walk fanned out (`visitRetired`: every descendant of the
     * root to `depth` levels down, drawing the first rendered node on each chain), the pass now only
     * RECORDS the root — position, level, depth — and resolves every root at once after the walk by
     * iterating the retired chunks (deps.retiredMeshesAt) and asking which recorded root each sits
     * under. Same verdicts (a retired chunk draws iff it lies under a root within its depth and no
     * retired ancestor between it and the root draws), without visiting a single position that has
     * no retired chunk in it. Keyed by packChunkKeyLod; the value is the deepest depth recorded for
     * that root, so a root reached twice keeps its wider reach.
     */
    const retiredRoots = new Map<number, number>();
    /** Deepest depth any root records — bounds the ancestor probes in resolveRetired. */
    let retiredMaxDepth = 0;
    const recordRetiredRoot = (cx: number, cy: number, cz: number, lod: number, depth: number): void => {
      const k = packChunkKeyLod(cx, cy, cz, lod);
      const prev = retiredRoots.get(k);
      if (prev === undefined || prev < depth) retiredRoots.set(k, depth);
      if (depth > retiredMaxDepth) retiredMaxDepth = depth;
    };

    /**
     * Resolve the recorded roots against the retired chunks. Coarsest level first, so a drawn
     * retired chunk is in `drawn` before any of its retired descendants are considered — that is
     * the old top-down "first rendered node wins" order, chain by chain.
     */
    const resolveRetired = (): void => {
      if (retiredRoots.size === 0) return;
      const drawn = new Set<number>();
      for (let lod = 31; lod >= 0; lod--) {
        // A lod-0 chunk outside its own box stands in lod 1's volume; drawing it is surrendered again.
        if (lod === 0 && levels[0].lod === 0) break;
        const retired = this.deps.retiredMeshesAt(lod);
        if (!retired) continue;
        for (const packed of retired) {
          const [x, y, z] = unpackCoord(packed);
          // Under a recorded root? The root sits `d` levels up, and must reach at least `d` down.
          let rootLod = -1;
          for (let d = 0; d <= retiredMaxDepth; d++) {
            const depth = retiredRoots.get(packChunkKeyLod(x >> d, y >> d, z >> d, lod + d));
            if (depth !== undefined && depth >= d) { rootLod = lod + d; break; }
          }
          if (rootLod < 0) continue;
          // A drawn retired ancestor between here and the root suppresses this chunk (no overlap).
          let suppressed = false;
          for (let d = 1; d <= rootLod - lod && !suppressed; d++) {
            if (drawn.has(packChunkKeyLod(x >> d, y >> d, z >> d, lod + d))) suppressed = true;
          }
          if (suppressed) continue;
          drawn.add(packChunkKeyLod(x, y, z, lod));
          out.set(x, y, z, lod, true);
        }
      }
    };

    /**
     * THE NO-STEAL PROBE: is any in-range descendant of this node CONTRIBUTING PIXELS right now?
     *
     * A coarse node that has just meshed may legitimately cover volume nothing is drawing — that
     * is how a view fills coarse-first — but it may not TAKE THE SCREEN from finer chunks the user
     * can already see. Cold start makes that the common case, because the walk hands the screen to
     * lod 0 before any coarse mesh exists (nothing coarse is rendered, so nothing can cover), and
     * the first coarse mesh to land over a half-streamed octet would otherwise draw and suppress
     * its whole subtree — then release again when the octet completes. Show → hide → show, per
     * octet: the reported initial-load flicker.
     *
     * SUBTREE-DEEP, because the loss is the same at any depth: a lod-2 node stealing from a
     * displayed lod-0 grandchild removes exactly the pixels a direct child would have.
     *
     * Memoized per (position, level) on the shells' lattice like `covered`, and it descends only
     * through IN-RANGE
     * positions, so the whole probe is bounded by the walk's own node count however many takeover
     * candidates ask. Short-circuits on the first displayed descendant.
     */
    const anyDisplayedBelow = (cx: number, cy: number, cz: number, li: number): boolean => {
      if (li === 0 || !this.deps.isDisplayed) return false; // no finer level / no display layer
      const slot = at(li, cx, cy, cz);
      if (slot >= 0) {
        const memo = lat[li].displayed[slot];
        if (memo !== 0) return memo === 1;
      }
      const childLod = levels[li - 1].lod;
      const bx = cx << 1, by = cy << 1, bz = cz << 1;
      let found = false;
      if (octetInRange(bx, by, bz, li - 1)) {
        for (let i = 0; i < 8 && !found; i++) {
          const chx = bx + (i & 1), chy = by + ((i >> 1) & 1), chz = bz + ((i >> 2) & 1);
          found = this.deps.isDisplayed(chx, chy, chz, childLod)
            || anyDisplayedBelow(chx, chy, chz, li - 1);
        }
      }
      if (slot >= 0) lat[li].displayed[slot] = found ? 1 : 2;
      return found;
    };

    /**
     * MAY THIS RENDERED NODE TAKE OVER? Asked at every point where a node with an applied mesh is
     * about to draw over a subtree its children do NOT cover, and answered by the no-steal rule.
     *
     * Only when the finer level is in range (`hasChild`): with the children out of range this is
     * the rim or a range that just shrank, where the coarse node is the rightful owner and the
     * retired-fallback machinery deliberately lets it supersede leftover finer chunks. Restricting
     * the rule to `hasChild` is what keeps the far view filling coarse-first.
     */
    const mayTakeOver = (cx: number, cy: number, cz: number, li: number, hasChild: boolean): boolean =>
      !hasChild || !anyDisplayedBelow(cx, cy, cz, li);

    /**
     * THE RELEASE RULE, in one place: may this node hand its volume to the finer level? Every
     * caller (the normal path, the vertical-clip path) must ask THIS — two copies of the rule is
     * precisely how the walk drifted before. `hasChild` false means the finer level isn't in range
     * here (the rim), which is never a release.
     */
    const childrenCover = (
      cx: number, cy: number, cz: number, li: number, hasChild: boolean,
    ): boolean => {
      // `hasChild` is passed in rather than returned: every caller has already read it off the
      // lattice (octetInRange), and returning a pair meant an object allocation per node per caller.
      if (!hasChild) return true;
      const bx = cx << 1, by = cy << 1, bz = cz << 1;
      let allCovered = true;
      for (let i = 0; i < 8; i++) {
        const chx = bx + (i & 1), chy = by + ((i >> 1) & 1), chz = bz + ((i >> 2) & 1);
        if (!covered(chx, chy, chz, li - 1)) allCovered = false;
      }
      // Release also waits on the vertical surface anchor — the finer surface may sit in a row
      // OUTSIDE this node's own subtree (see finerSurfaceCovered).
      if (allCovered && !finerSurfaceCovered(cx, cz, li)) allCovered = false;
      return allCovered;
    };

    /**
     * THE NEXT STEP DOWN. A node that is about to DRAW over volume its children do not cover is,
     * by definition, one step of a decimation staircase — and the step after it is those children.
     * Ask for their meshes here, because a drawn node RETURNS without recursing (that is how it
     * suppresses the detail beneath it), so nothing below it is ever visited and nothing below it
     * could otherwise ask.
     *
     * This is what makes the staircase work at all when coarse meshes are built on demand: without
     * it a pan goes from the coarsest drawn level straight to the base level with no intermediate
     * step, because the intermediate level is never visited AND never range-eligible (a finer level
     * is in range, so it reads as "owned"). One level deep and at most 8 nodes, so the work tracks
     * how much is actively transitioning rather than the size of the onion.
     */
    const requestFinerStep = (bx: number, by: number, bz: number, li: number): void => {
      const childLod = levels[li - 1].lod;
      if (!octetInRange(bx, by, bz, li - 1)) return;
      for (let i = 0; i < 8; i++) {
        const chx = bx + (i & 1), chy = by + ((i >> 1) & 1), chz = bz + ((i >> 2) & 1);
        this.wantsMesh.add(packChunkKeyLod(chx, chy, chz, childLod));
      }
    };

    const visit = (cx: number, cy: number, cz: number, li: number): void => {
      const lod = levels[li].lod;
      this.lastVisitsByLod[lod] = (this.lastVisitsByLod[lod] ?? 0) + 1;
      if (li === 0) {
        // Coarsening dropped a finer level from the onion while this coarse base node has no mesh
        // yet (the zoom-out step timeout): the old, finer base chunks are retired but still
        // resident — draw THEM under the node instead of showing a hole. The node itself is
        // selected FALSE in that mode (it has nothing to draw, and a true verdict would formally
        // overlap its retired descendants); the pass after it meshes flips it true and suppresses
        // them. Otherwise base nodes are always selected — the caller's render gate decides.
        const retireFallback =
          lod > 0 && !this.deps.isRendered(cx, cy, cz, lod) && !this.deps.isCoarseCovered(cx, cy, cz, lod);
        out.set(cx, cy, cz, lod, !retireFallback);
        if (retireFallback) {
          const bx = cx << 1, by = cy << 1, bz = cz << 1;
          for (let i = 0; i < 8; i++) {
            recordRetiredRoot(bx + (i & 1), by + ((i >> 1) & 1), bz + ((i >> 2) & 1), lod - 1, 1);
          }
        }
        return;
      }
      const bx = cx << 1, by = cy << 1, bz = cz << 1;
      // ONE test for the octet — see octetInRange. Every recursion loop below reads it.
      const childOctet = octetInRange(bx, by, bz, li - 1);

      // A vertically-clipped (provably sky/solid) coarse node normally has no mesh, so it draws
      // nothing but must STILL recurse: at the base level "provably solid" is not base coverage
      // (caves can make underground chunks visible), so meshed descendants must not be suppressed.
      //
      // If such a node DOES have an applied mesh, though, it is a usable fallback and must not be
      // force-hidden: a chunk requested before its column probe arrived (or whose clip changed as
      // the center moved) was left `sel=F` with a finished mesh sitting directly over volume that
      // showed nothing ([lod-audit]: `l2(…)[complete,sel=F,hid]` above a hole). It draws on the
      // same condition as any other node — its children don't cover it — and then suppresses its
      // subtree like any drawn node, so this cannot introduce an overlap.
      if (this.deps.isCoarseCovered(cx, cy, cz, lod)) {
        if (this.deps.isRendered(cx, cy, cz, lod)) {
          const clippedCovered = childrenCover(cx, cy, cz, li, childOctet);
          // Same no-steal rule as the normal path below: a clipped node with a usable mesh is a
          // legitimate fallback over volume nobody is drawing, not a licence to blank displayed
          // descendants.
          if ((!childOctet || !clippedCovered) && mayTakeOver(cx, cy, cz, li, childOctet)) {
            if (childOctet) {
              this.deficitByLod[lod] = (this.deficitByLod[lod] ?? 0) + 1;
              requestFinerStep(bx, by, bz, li);
            }
            out.set(cx, cy, cz, lod, true);
            return;
          }
        }
        out.set(cx, cy, cz, lod, false);
        for (let i = 0; childOctet && i < 8; i++) {
          visit(bx + (i & 1), by + ((i >> 1) & 1), bz + ((i >> 2) & 1), li - 1);
        }
        return;
      }

      const hasChild = childOctet;
      const allCovered = childrenCover(cx, cy, cz, li, hasChild);
      if (!hasChild || !allCovered) {
        // This node should cover the region — but it may only SUPPRESS its descendants when it has
        // an applied mesh to show. Hiding already-visible children behind a parent that draws
        // nothing would make meshed terrain vanish while the coarse fallback streams in (rings can
        // be enabled over a live world). No mesh yet → draw whatever descendants exist (partial
        // coverage, exactly the pre-LOD streaming look) and swap to this node when it applies.
        //
        // A mesh is still not enough: NO-STEAL. If anything in this subtree is already on screen,
        // this node covers nothing the user isn't already seeing, and drawing it would blank those
        // pixels until the octet completes — cold start's lod0 → coarse → lod0 flicker. Yield and
        // recurse instead; the descendants keep drawing, and the takeover simply never becomes
        // necessary because the octet finishing IS the swap.
        const rendered = this.deps.isRendered(cx, cy, cz, lod);
        if (rendered && mayTakeOver(cx, cy, cz, li, hasChild)) {
          if (hasChild) {
            this.deficitByLod[lod] = (this.deficitByLod[lod] ?? 0) + 1;
            requestFinerStep(bx, by, bz, li);
          }
          out.set(cx, cy, cz, lod, true);
          return;
        }
        // WOULD DRAW IF IT COULD. Recorded so the mesher can build it on demand — see wantsMesh.
        // The no-steal test gates it for the same reason it gates the takeover: a node with
        // displayed descendants is yielding deliberately and a mesh would be built for nothing. It
        // is cheap here — the ring answers `!hasChild` without descending at all, and the nodes that
        // do descend are the few at the fine level's frontier.
        if (!rendered && mayTakeOver(cx, cy, cz, li, hasChild)) {
          this.wantsMesh.add(packChunkKeyLod(cx, cy, cz, lod));
        }
        out.set(cx, cy, cz, lod, false);
        // The octet invariant makes children all-in or all-out of range: when they're out
        // (!hasChild — the rim, or a range that just shrank), fall back to retired resident
        // chunks in this volume rather than drawing nothing.
        // Recorded, not walked — see retiredRoots. Depth 2: the finer level and two below it.
        const finerLod = levels[li - 1].lod;
        for (let i = 0; i < 8; i++) {
          const chx = bx + (i & 1), chy = by + ((i >> 1) & 1), chz = bz + ((i >> 2) & 1);
          if (childOctet) visit(chx, chy, chz, li - 1);
          else recordRetiredRoot(chx, chy, chz, finerLod, 2);
        }
        return;
      }
      out.set(cx, cy, cz, lod, false);
      for (let i = 0; childOctet && i < 8; i++) {
        visit(bx + (i & 1), by + ((i >> 1) & 1), bz + ((i >> 2) & 1), li - 1);
      }
    };

    for (const packed of levels[last].inRange) {
      const [x, y, z] = unpackCoord(packed);
      visit(x, y, z, last);
    }
    resolveRetired();
    return out;
  }
}
